import React, { useMemo, useState } from "react";
import {
  Button,
  CustomDropdown,
  Modal,
  TextField,
  fieldLabelClasses,
} from "../../../components/ui";
// A deep import rather than the clients barrel: that barrel pulls in the
// client pages, and the client module imports this dialog back for its Plans
// tab.
import ClientPickerField from "../../clients/components/ClientPickerField";
import { useMealPlanTypes } from "../hooks/useMealPlanTypes";
import type { NewPlan } from "../types/plans.types";

/** Which half of the dialog is showing. */
type Step = "client" | "details";

/**
 * The second step's plan type, as its own component for the same reason
 * `ClientPickerField` is one: the catalog behind it is fetched with the step,
 * so a dialog closed on the first step never asks for a list it will not show.
 */
const PlanTypeField: React.FC<{
  planTypeId: number | null;
  onChange: (id: number, name: string) => void;
}> = ({ planTypeId, onChange }) => {
  const { options, status } = useMealPlanTypes();

  const rows = useMemo(
    () =>
      options.map((type) => ({ value: String(type.id), label: type.name })),
    [options]
  );

  const placeholder =
    status === "loading"
      ? "Loading plan types…"
      : status === "failed"
      ? "Could not load plan types"
      : "Select plan type";

  return (
    <div>
      <label id="create-plan-type-label" className={`${fieldLabelClasses} mb-1.5`}>
        Select plan
      </label>
      {/* `CustomDropdown` rather than `DropdownField`, for the same reason the
          client step uses one: the row has to carry the id, and the labelled
          field only deals in strings. The create sends `plan_type: 1`, not
          `plan_type: "Regular"`. */}
      <CustomDropdown
        options={rows}
        value={planTypeId == null ? "" : String(planTypeId)}
        onChange={(value) =>
          onChange(
            Number(value),
            rows.find((row) => row.value === value)?.label ?? ""
          )
        }
        placeholder={placeholder}
        disabled={status !== "ready"}
        searchable
        aria-labelledby="create-plan-type-label"
      />
    </div>
  );
};

export interface CreatePlanModalProps {
  onClose: () => void;
  /**
   * Given the finished answers — creating the plan is the caller's to do, and
   * it is a request now, so this may be awaited. The dialog stays open and
   * usable until it resolves, then the caller closes it.
   */
  onCreate: (plan: NewPlan) => void | Promise<void>;
  /**
   * The client the plan is for, when that is already known. Set from a
   * client's own screen, where asking which client it is for would be asking
   * something the page already knows; the dialog then opens on its second step.
   */
  client?: { id: string; name: string };
}

/**
 * The two-step Create new plan dialog: who the plan is for, then what it is
 * called and what it is for.
 *
 * Mounted only while it is open, so the client list is fetched when the dialog
 * opens rather than with the page behind it, and every answer is forgotten on
 * close instead of being left over for the next plan.
 */
const CreatePlanModal: React.FC<CreatePlanModalProps> = ({
  onClose,
  onCreate,
  client,
}) => {
  const [step, setStep] = useState<Step>(client ? "details" : "client");
  const [clientId, setClientId] = useState(client?.id ?? "");
  // Held beside the id rather than looked up from the list on render, so the
  // details step does not need the list the step before it loaded.
  const [clientName, setClientName] = useState(client?.name ?? "");
  const [name, setName] = useState("");
  const [planTypeId, setPlanTypeId] = useState<number | null>(null);
  const [planType, setPlanType] = useState("");
  // Creating is a request now, and a second one would make a second plan.
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (planTypeId == null || saving) return;

    setSaving(true);
    try {
      await onCreate({
        clientId,
        clientName,
        name: name.trim(),
        planTypeId,
        planType,
      });
    } finally {
      // The dialog is unmounted by the caller on success, so this only ever
      // runs after a failure — which is exactly when it has to be usable again.
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title="Create new plan"
      onClose={onClose}
      className="max-w-[526px]"
      footer={
        <>
          <Button
            variant="secondary"
            size="md"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </Button>
          {step === "client" ? (
            <Button
              size="md"
              disabled={!clientId}
              onClick={() => setStep("details")}
            >
              Next
            </Button>
          ) : (
            <Button
              size="md"
              disabled={!name.trim() || planTypeId == null || saving}
              onClick={handleCreate}
            >
              {saving ? "Creating…" : "Create plan"}
            </Button>
          )}
        </>
      }
    >
      {step === "client" ? (
        // Its own component, so the roster is only fetched when this step is
        // shown — see `ClientPickerField`.
        <ClientPickerField
          id="create-plan-client"
          label="Choose client for plan"
          clientId={clientId}
          onChange={(id, selectedName) => {
            setClientId(id);
            setClientName(selectedName);
          }}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <TextField
            label="Write the plan name"
            value={name}
            onValueChange={setName}
            placeholder="Untitle plan"
            maxLength={80}
            autoFocus
          />
          <PlanTypeField
            planTypeId={planTypeId}
            onChange={(id, typeName) => {
              setPlanTypeId(id);
              setPlanType(typeName);
            }}
          />
        </div>
      )}
    </Modal>
  );
};

export default CreatePlanModal;
